import type { ReactNode } from 'react'
import toast from 'react-hot-toast'

import { GlobalAccountStatus } from '@ankor-io/common/types/wallet/global.account'
import { Button, Loading, Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@ankor-io/ui-components'

import { useConnectedAccountGlobalAccountsQuery, useDownloadConnectedAccountStatementMutation } from '@/queries'

const COLUMN_COUNT = 6

export function ConnectedAccountGlobalAccounts({
  connectedAccountId,
  orgId,
  vesselId,
}: {
  connectedAccountId: string
  orgId: string
  vesselId: string
}) {
  const {
    data: globalAccounts,
    isPending,
    isError,
  } = useConnectedAccountGlobalAccountsQuery(orgId, vesselId, connectedAccountId)
  const downloadStatement = useDownloadConnectedAccountStatementMutation()

  const handleDownloadStatement = (globalAccountId: string) => {
    toast.promise(() => downloadStatement.mutateAsync({ orgId, vesselId, connectedAccountId, globalAccountId }), {
      loading: 'Generating statement…',
      success: 'Statement downloaded.',
      error: 'Failed to generate statement.',
    })
  }

  const renderMessageRow = (content: ReactNode) => (
    <TableRow>
      <TableCell
        colSpan={COLUMN_COUNT}
        className="text-center"
      >
        <div className="flex items-center justify-center">{content}</div>
      </TableCell>
    </TableRow>
  )

  return (
    <div>
      <h4 className="font-bold text-sm">Global accounts</h4>
      <p className="text-sm text-gray-500 mt-0.5 dark:text-gray-400">
        Download the official Airwallex account statement (proof of account ownership) for a global account.
      </p>

      <div className="overflow-x-auto mt-3">
        <Table className="max-w-[calc(100vw-2rem)] lg:max-w-[calc(100vw-20rem)]">
          <TableHead>
            <TableRow>
              <TableHeader>Nickname</TableHeader>
              <TableHeader>Account name</TableHeader>
              <TableHeader>Currency</TableHeader>
              <TableHeader>IBAN / Account number</TableHeader>
              <TableHeader>Status</TableHeader>
              <TableHeader>
                <span className="sr-only">Actions</span>
              </TableHeader>
            </TableRow>
          </TableHead>
          <TableBody>
            {isPending
              ? renderMessageRow(<Loading />)
              : isError
                ? renderMessageRow(<p>Failed to load global accounts</p>)
                : globalAccounts.length === 0
                  ? renderMessageRow(<p>No global accounts found</p>)
                  : globalAccounts.map((globalAccount) => (
                      <TableRow key={globalAccount.id}>
                        <TableCell className="text-sm font-medium text-gray-900 dark:text-gray-50">
                          {globalAccount.nickName}
                        </TableCell>
                        <TableCell className="text-sm text-gray-900 dark:text-gray-100">
                          {globalAccount.accountName}
                        </TableCell>
                        <TableCell className="text-sm text-gray-900 dark:text-gray-100">
                          {globalAccount.currencies.join(', ')}
                        </TableCell>
                        <TableCell className="text-sm text-gray-900 dark:text-gray-100">
                          {globalAccount.iban || globalAccount.accountNumber}
                        </TableCell>
                        <TableCell className="text-sm text-gray-900 dark:text-gray-100">
                          {globalAccount.status}
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            plain
                            className="disabled:cursor-not-allowed"
                            onClick={() => handleDownloadStatement(globalAccount.id)}
                            disabled={
                              globalAccount.status !== GlobalAccountStatus.ACTIVE || downloadStatement.isPending
                            }
                          >
                            Download statement
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
          </TableBody>
        </Table>
      </div>
    </div>
  )
}
